/**
 * @license
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

/**
 * Shell Selector - Entry point that determines which shell to load
 *
 * This module implements the Flighting + localStorage Cache approach:
 * 1. Check localStorage cache first for instant startup (no network wait)
 * 2. Immediately bootstrap the appropriate shell based on cached value
 * 3. After authentication, ShellFlightingService fetches fresh flighting value and updates cache
 *
 * The shell preference only changes on the next page load after flighting updates.
 *
 * Note: This module runs BEFORE Angular bootstraps, so we cannot use Angular DI
 * or the GdcoFlighting service. The flighting check happens after bootstrap via
 * ShellFlightingService in shellv2.component.ts.
 *
 * For testing: Use the browser console to set shell preference:
 *   __GDCO_SHELL_TESTING__.setDccShellPreference(true)  - Enable DCC Shell (sets testing override)
 *   __GDCO_SHELL_TESTING__.setDccShellPreference(false) - Enable ShellV2 (sets testing override)
 *   __GDCO_SHELL_TESTING__.clearDccShellPreference()    - Clear preference and override (flighting resumes)
 *   __GDCO_SHELL_TESTING__.getDccShellPreference()      - Check current preference
 *   __GDCO_SHELL_TESTING__.isTestingOverrideActive()    - Check if testing override is blocking flighting
 *   __GDCO_SHELL_TESTING__.clearTestingOverride()       - Clear only the override, keep preference
 */

const SHELL_CACHE_KEY = 'dcc-shell-preference';
const SHELL_TESTING_OVERRIDE_KEY = 'dcc-shell-testing-override';

interface ShellCache {
  useDccShell: boolean;
  timestamp: number;
}

/**
 * Retrieves the cached shell preference from localStorage
 * @returns The cached shell preference, or null if no cache exists or cache is invalid
 */
function getCachedShellPreference(): boolean | null {
  try {
    const raw = localStorage.getItem(SHELL_CACHE_KEY);
    if (!raw) return null;

    const cached: ShellCache = JSON.parse(raw);
    return cached.useDccShell;
  } catch {
    // Invalid cache data - return null to use default
    return null;
  }
}

/**
 * Updates the localStorage cache with the new shell preference
 * @param useDccShell - Whether to use DCC Shell
 */
function updateCache(useDccShell: boolean): void {
  const cache: ShellCache = {
    useDccShell,
    timestamp: Date.now()
  };
  localStorage.setItem(SHELL_CACHE_KEY, JSON.stringify(cache));
}

/**
 * Expose helper functions on window for testing via browser console
 * Namespaced under __GDCO_SHELL_TESTING__ to prevent naming collisions
 *
 * When setDccShellPreference is called, it also sets a testing override flag
 * that prevents the flighting service from overwriting the manual preference.
 * This allows developers to test shell switching without flighting interference.
 */
(window as any).__GDCO_SHELL_TESTING__ = {
  setDccShellPreference: function (useDccShell: boolean): void {
    updateCache(useDccShell);
    // Set testing override to prevent flighting from overwriting manual preference
    localStorage.setItem(SHELL_TESTING_OVERRIDE_KEY, 'true');
  },
  clearDccShellPreference: function (): void {
    localStorage.removeItem(SHELL_CACHE_KEY);
    // Also clear the testing override so flighting can sync again
    localStorage.removeItem(SHELL_TESTING_OVERRIDE_KEY);
  },
  getDccShellPreference: function (): boolean | null {
    const cached = getCachedShellPreference();
    return cached;
  },
  isTestingOverrideActive: function (): boolean {
    return localStorage.getItem(SHELL_TESTING_OVERRIDE_KEY) === 'true';
  },
  clearTestingOverride: function (): void {
    localStorage.removeItem(SHELL_TESTING_OVERRIDE_KEY);
  }
};

/**
 * Main initialization function that orchestrates shell selection and bootstrap
 *
 * Shell Selection:
 * - Defaults to ShellV2 (false) if no localStorage preference is set
 * - Users must explicitly opt-in to DCC Shell via setDccShellPreference(true) or flighting
 * - This ensures most users see no change from their normal experience
 *
 * Flighting Integration:
 * - After bootstrap, ShellFlightingService.syncFlightingToCache() is called
 * - This updates the cache for the next page load based on the 'dcc-shell' flight flag
 * - See shell-flighting.service.ts for implementation details
 */
async function initializeApp(): Promise<void> {
  // Check cached value first (instant, no network)
  const cached = getCachedShellPreference();

  // Default to ShellV2 (false) - users must opt-in to DCC Shell
  // This ensures existing users see no change from their normal experience
  let useDccShell = cached ?? false;

  // Force DCC Shell for React extension routes that only exist in DCC Shell.
  // Without this, a cold first visit (no cache) to e.g. /tasks-details/12345
  // would boot ShellV2, which doesn't know that route and shows a 404.
  // Note: We only override in memory for this boot — we don't persist to cache
  // so subsequent visits to other routes still respect flighting/manual preference.
  if (!useDccShell) {
    const reactExtensionPaths = ['/tasks-details', '/labor-v2'];
    const path = window.location.pathname;
    if (reactExtensionPaths.some(p => path === p || path.startsWith(p + '/'))) {
      useDccShell = true;
    }
  }

  // Set global flag so Angular components can detect which shell is active
  // This allows shellv2.component to conditionally hide its chrome when inside DCC Shell
  (window as any).__GDCO_USE_DCC_SHELL__ = useDccShell;

  // Bootstrap the appropriate shell immediately
  if (useDccShell) {
    document.getElementById('_skip')?.remove();

    // DCC Shell via Module Federation
    // This imports bootstrap.ts which loads DCC Shell from CDN
    await import('./bootstrap');
  } else {
    // Standard ShellV2
    // Note: We use main-shellv2.ts instead of main.ts because the test-mf
    // build config has a fileReplacement that swaps main.ts with shell-selector.ts
    await import('./main-shellv2');
  }

  // Flighting sync happens after bootstrap in ShellFlightingService
  // (injected into shellv2.component.ts or DCC Shell equivalent)
}

// Start the application
initializeApp();

// Empty export to make this file a module (required for dynamic imports)
export {};
